import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { PaperButtonComponent, PaperIconComponent } from '@ricoh-paper/angular';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../../core/analytics/events';
import { PlatformCustomerService } from '../../../../core/services/platform-customer.service';
import { FocusTrapDirective } from '../../../../shared/directives/focus-trap.directive';
import {
  nextActiveIndex,
  rankCustomers,
} from './workspace-switcher-filter.util';
import { WORKSPACE_SWITCHER_CLOSE } from './workspace-switcher-dialog.tokens';

const SEARCH_DEBOUNCE_MS = 400;

/**
 * The searchable "Switch workspace" dialog, for people who can reach many
 * customers (RDx support staff with all-customers access reach every
 * customer in the region).
 *
 * The inline list in the profile panel stays for a handful of workspaces;
 * past `SWITCHER_DIALOG_THRESHOLD` the panel opens this instead. Search is
 * focused on open; names that start with the query rank first
 * (`rankCustomers`); ↑/↓ move, Enter switches; at most
 * `SWITCHER_DIALOG_RENDER_LIMIT` rows render and the footer says how many
 * matched.
 *
 * ⚠️ THE TYPED TEXT NEVER LEAVES THE BROWSER — the same contract as the
 * inline search. Workspace names are this person's client list, so the
 * input is `ph-mask`ed and the SEARCH event carries only lengths and counts.
 */
@Component({
  selector: 'im-workspace-switcher-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe, PaperButtonComponent, PaperIconComponent, FocusTrapDirective],
  host: { class: 'ph-no-capture' },
  templateUrl: './workspace-switcher-dialog.component.html',
  styleUrl: './workspace-switcher-dialog.component.scss',
})
export class WorkspaceSwitcherDialogComponent {
  protected readonly platformCustomers = inject(PlatformCustomerService);
  private readonly analytics = inject(AnalyticsService);
  private readonly closeDialog = inject(WORKSPACE_SWITCHER_CLOSE);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly listboxId = 'ws-switcher-listbox';
  protected readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('search');

  protected readonly currentCustomerId = this.platformCustomers.currentCustomerId();
  protected readonly query = signal('');
  protected readonly activeIndex = signal(-1);
  protected readonly switchingId = signal<string | null>(null);
  protected readonly logoErrors = signal<Record<string, boolean>>({});

  protected readonly ranked = computed(() =>
    rankCustomers(this.platformCustomers.customers(), this.query(), this.currentCustomerId),
  );
  protected readonly truncated = computed(
    () => this.ranked().total > this.ranked().items.length,
  );
  protected readonly activeId = computed(() => {
    const item = this.ranked().items[this.activeIndex()];
    return item ? `ws-switcher-option-${item.id}` : null;
  });

  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Opened fresh: a switch that failed earlier, elsewhere, is not news.
    this.platformCustomers.clearSwitchError();
    this.destroyRef.onDestroy(() => {
      if (this.searchTimer) clearTimeout(this.searchTimer);
    });
  }

  protected onQuery(value: string): void {
    this.query.set(value);
    this.activeIndex.set(value.trim() ? 0 : -1);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      const len = this.query().trim().length;
      if (len > 0) {
        this.analytics.capture(ANALYTICS_EVENTS.ORG_SWITCHER_SEARCHED, {
          query_length: len,
          customer_count: this.platformCustomers.customers().length,
          result_count: this.ranked().total,
        });
      }
    }, SEARCH_DEBOUNCE_MS);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const length = this.ranked().items.length;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex.update((i) =>
        nextActiveIndex(i, event.key === 'ArrowDown' ? 1 : -1, length),
      );
      this.scrollActiveIntoView();
    } else if (event.key === 'Enter') {
      const item = this.ranked().items[this.activeIndex()];
      if (item) {
        event.preventDefault();
        void this.choose(item.id);
      }
    }
  }

  protected async choose(id: string): Promise<void> {
    if (!id || id === this.currentCustomerId || this.switchingId()) return;
    this.switchingId.set(id);
    // On success the app hard-reloads; only a failure comes back here.
    const ok = await this.platformCustomers.switchTo(id, 'switcher-dialog');
    if (!ok) this.switchingId.set(null);
  }

  protected close(): void {
    this.closeDialog();
  }

  protected onLogoError(id: string): void {
    this.logoErrors.update((m) => ({ ...m, [id]: true }));
  }

  private scrollActiveIntoView(): void {
    const id = this.activeId();
    if (!id) return;
    queueMicrotask(() => {
      document.getElementById(id)?.scrollIntoView({ block: 'nearest' });
    });
  }
}
